import {
	Grid,
	Box,
	TextField,
	MenuItem,
	Button,
	Modal,
	Typography,
	Card,
	Stack,
} from "@mui/material";
import { GenericTable } from "../../components/table/GenericTable";
import { mapperColumnsDetails } from "./config";
import { useCallback, useEffect, useState } from "react";
import {
	getIntegrationFileInfo,
	getMapperInformation,
	saveSalesMapper,
} from "../../services/integrationService";
import {
	IntegrationFileInfo,
	RemoteStreamLoadFile,
	IntegrationMapper,
	SalesMapper,
	SalesMapperRequest,
	mappingObject,
	functionMap,
	fieldInfoMap,
	parameterInfo,
	SalesMapperFieldConfig,
} from "../../types/types";
import { useDispatch, useSelector } from "react-redux";
import { IntegrationActions } from "../../stateManagement/actions/integrationDashboardActions";
import { Loader } from "../../components/loader/Loader";
import { EntityTransformer } from "./EntityTransformer";
import { isUndefinedorEmpty } from "./UnregisteredFunctions";
import { openPopup } from "../../stateManagement/actions/popupActions";

export const funcNamePattern =
	"function\\s+([_$a-zA-Z][\\w$]*)\\s*\\(((\\s*[_$a-zA-Z][\\w$]*\\s*,)*)(\\s*[_$a-zA-Z][\\w$]*\\s*)\\)";

function convertToFunctioninfo(functionBody: string):(functionMap|null) {
	const match = functionBody.match(funcNamePattern) || [];
	if (isUndefinedorEmpty(match) || match.length !== 5) {
		return null;
	}
	const funcName = match[1];
	const leadParams = match[2];
	const trailParams = match[4];

	let functionInfo: functionMap = {} as functionMap;
	let parametersInfo: parameterInfo[] = [] as parameterInfo[];

	functionInfo.id = funcName.trim();
	functionInfo.parametersInfo = parametersInfo;
  	functionInfo.functionBody = functionBody;
	if (!isUndefinedorEmpty(leadParams) && leadParams.length > 0) {
		for (let paramStr of leadParams.split(",")) {
			if (!isUndefinedorEmpty(paramStr) && paramStr.trim().length > 0) {
				let paramObj: parameterInfo = {} as parameterInfo;
				paramObj.parameterName = paramStr.trim();
				paramObj.isVariableArgument = false;
				paramObj.required = false;
				paramObj.type = "autogenerated";
				paramObj.parameterDescription = "autogenerated";
				parametersInfo.push(paramObj);
			}
		}
	}
	let paramObj: parameterInfo = {} as parameterInfo;
	paramObj.parameterName = trailParams.trim();
	paramObj.isVariableArgument = false;
	paramObj.required = false;
	paramObj.type = "autogenerated";
	paramObj.parameterDescription = "autogenerated";
	parametersInfo.push(paramObj);
	return functionInfo;
}

const defaultMapperInfo: IntegrationMapper = {
	id: "",
	mapperName: "Create New",
	entityName: "Sales",
	type: "db",
	config: "",
};

export function Transformer(props: {
	fieldList: fieldInfoMap[];
	remoteFileInfo: RemoteStreamLoadFile;
	mapperConfig: SalesMapperFieldConfig[];
	increment: () => void;
	decrement: () => void;
}) {
	const dispatch = useDispatch();

	const remoteFileInfoFromStore:RemoteStreamLoadFile = useSelector((state: any) => {
		let registry = state.integrationRegistry;
		return {
			delimiter: registry.delimiter,
			encoding: registry.encoding,
			entityName: registry.entityName,
			profile: registry.profileName,
			profileName: registry.profileName,
			mapperName: registry.mapperName,
			lob: registry.lob,
			startsWith: registry.startsWith,
			passKey: registry.passKey,
			fileName:"",
			name:"",
			size:"",
			lastmodified:"",
		};
	});

	const selectedMapper = useSelector((state: any) => remoteFileInfoFromStore.entityName === "Sales" ? state.integrationRegistry.mapperName : state.integrationRegistry.transformerId);

	const [fileInfo, setFileInfo] = useState<IntegrationFileInfo[]>([]); 
	const [allMapperInfo, setAllMapperInfo] = useState<IntegrationMapper[]>([]); 
	const [open, setOpen] = useState<boolean>(false);
	const [loader, setLoader] = useState(true);
	const [unRegFunc, setUnRegFunc] = useState<Map<string, string>>(new Map());

	// create functioninfo json object for each unregistered function for parameter modal
	const unRegFunctionInfoArr: (functionMap)[] = Array.from(unRegFunc.values()).reduce((res:functionMap[],functionInfoStr:string) => {
		const functionInfo = convertToFunctioninfo(functionInfoStr);
		if(functionInfo) res.push(functionInfo);
		return res;
	},[])

	function updateUnregisteredMap(activeMapper:IntegrationMapper){
		let list: mappingObject[] = [];
		Object.assign(list, activeMapper.config);
		const unregisteredFunctionMap = new Map<string, string>();
		list.forEach((s: mappingObject) => {
			if (
				s.conversions &&
				s.conversions.length > 0 &&
				s.conversions[0].functionName &&
				s.conversions[0].unRegisteredFunction
			)
				unregisteredFunctionMap.set(
					s.conversions[0].functionName,
					s.conversions[0].unRegisteredFunction
				);
		});
		setUnRegFunc(unregisteredFunctionMap);
	}

	const fetchIntegrationFileMeta = useCallback(async () => {
		try{let data = await getIntegrationFileInfo({
			...remoteFileInfoFromStore,
			...props.remoteFileInfo,
		} as RemoteStreamLoadFile);
		setFileInfo(data);}
		catch (e:any) {
			openPopup('Error',e.message);
		}
	}, [props.remoteFileInfo, remoteFileInfoFromStore]);

	const fetchMapperInformation = useCallback(async () => {
		let data: IntegrationMapper[] = await getMapperInformation(
			remoteFileInfoFromStore.entityName,
			remoteFileInfoFromStore.lob
		);
		data.unshift(defaultMapperInfo);
		setAllMapperInfo(data);
	}, [remoteFileInfoFromStore.entityName,remoteFileInfoFromStore.lob]);

	useEffect(() => {
		(async () => {
			fetchMapperInformation();
			await fetchIntegrationFileMeta();
			setLoader(false);
		})();
	}, []);

	useEffect(() => {
		if (selectedMapper === "") {
			updateUnregisteredMap(defaultMapperInfo)
		} else if (selectedMapper) {
			const mapperSelected: IntegrationMapper | undefined = allMapperInfo.find((element) => element.mapperName === selectedMapper);
			if (mapperSelected) { updateUnregisteredMap(mapperSelected) }
		}
	}, [allMapperInfo, selectedMapper]);

	const parseConfig = (mapperConfig: string | object) =>
		typeof mapperConfig == "object"
			? JSON.stringify(mapperConfig)
			: mapperConfig;

	const handleOpenTransformerButton = ()=>{ 
		const mapperObj : IntegrationMapper | undefined = allMapperInfo.find((element) =>element.mapperName ===selectedMapper) ;
		if(mapperObj && remoteFileInfoFromStore.entityName!=='Sales' && mapperObj.implementation !== 'com.applicate.services.mappingservice.transfomers.impl.IntegrationServiceTransfomer')
			openPopup("Alert",'Transformer is not a valid mapping transformer.')
		else{
			setOpen(true);
		}
	}
	
	function handleChange(event: React.ChangeEvent<HTMLInputElement>) { 
		if (event.target.value === defaultMapperInfo.mapperName) {
			handleDispatchMapper('','');
			return;
		}
		const mapperSelected: IntegrationMapper | undefined = allMapperInfo.find(
			(element) => element.mapperName === event.target.value
		);
		if (mapperSelected) {
			handleDispatchMapper(mapperSelected.mapperName,mapperSelected.config);
		}
	}
	
	const handleDispatchMapper = useCallback((mapperName:string,mapperConfig:string|object)=>{ 
		if (remoteFileInfoFromStore.entityName === "Sales") {
			dispatch(IntegrationActions.setMapper({mapperName: mapperName,mapper: parseConfig(mapperConfig)}))
			dispatch(IntegrationActions.setTransformerId(""));
		} else {
			dispatch(IntegrationActions.setTransformerId(mapperName));
			dispatch(IntegrationActions.setMapper({mapperName: "",mapper: ""}))
		}
	},[dispatch, remoteFileInfoFromStore.entityName])

	async function handleClose() {
		// handle close of modal
		setOpen(false);
		setLoader(true);
		await fetchMapperInformation();
		setLoader(false);
	}

	const handleNext = useCallback(() => {
		const mapper = allMapperInfo.find((element) => element.mapperName === selectedMapper);
		handleDispatchMapper(selectedMapper,mapper ? mapper.config : "")
		dispatch(IntegrationActions.setHeaders(fileInfo.length>0? fileInfo[0].headers:""));
		props.increment();
	}, [allMapperInfo, handleDispatchMapper, selectedMapper, dispatch, fileInfo, props]);

	return (
		<>
			<Stack>
				{loader ? (
					<Loader />
				) : (
					<Box>
						<Grid
							container
							spacing={4}
							padding={2}>
							<Grid
								item
								xs={12}>
								<GenericTable
									disableColumnFilter
									// disableColumnMenu
									// disableColumnSelector
									disableRowSelectionOnClick
									hideFooter
									autoHeight
									sx={{
										boxShadow: 1,
										padding: 2,
										borderRadius: 1,
										my: 4,
									}}
									rows={fileInfo}
									columns={mapperColumnsDetails}
									getRowId={(row:IntegrationFileInfo) => row.jobid || 0}
								/>
							</Grid>
							<Grid
								item
								xs>
								<TextField
									label="Choose Transformer(if already exists)"
									select
									name="mapperList"
									value={
										selectedMapper ||
										defaultMapperInfo.mapperName
									}
									onChange={handleChange}>
									{allMapperInfo.map((element) => (
										<MenuItem
											key={[
												element.mapperName,
												element.type,
											].join("-")}
											value={element.mapperName}>
											{element.mapperName}
										</MenuItem>
									))}
								</TextField>
							</Grid>
							<Grid
								display={"flex"}
								item
								alignItems={"center"}
								lg={6}
								md
								>
								<Button disabled={fileInfo.length===0 ? true:false} variant="outlined" onClick={handleOpenTransformerButton}>
									{selectedMapper.length === 0
										? "Create New"
										: "Edit"}
								</Button>
							</Grid>
						</Grid>
						{open &&
							(remoteFileInfoFromStore.entityName === "Sales" ? (
								<SalesMapperModal
									mapperConfig={props.mapperConfig}
									open={open}
									close={handleClose}
									selectedMapperInfo={
										allMapperInfo.find(
											(element) =>
												element.mapperName ===
												selectedMapper
										) || defaultMapperInfo
									}
									fileInfo={fileInfo[0]}
								/>
							) : (
							<EntityTransformer
								onClose={handleClose}
								open={open}
								isNew={selectedMapper.length === 0}
								unRegFunctionInfoArr={unRegFunctionInfoArr}
								transformer={allMapperInfo.find((element) =>element.mapperName ===selectedMapper) || defaultMapperInfo}
								fileInfo={fileInfo[0]}
								unRegFuncMap={unRegFunc}
								setUnRegFuncMap={setUnRegFunc}
                				columnList={props.fieldList}
							/>
						))}
						<Box
							width={"100%"}
							display="flex"
							marginTop={4}
							marginLeft={2}
							>
							<Button
								key="previous"
								className="navigation-btn bottom-bar-navigation-btn"
								onClick={props.decrement}
								variant="contained">
								{" "}
								Previous
							</Button>
							<Button
								key="next"
								className="navigation-btn bottom-bar-navigation-btn"
								onClick={handleNext}
								variant="contained">
								{" "}
								Next
							</Button>
						</Box>
					</Box>
				)}
			</Stack>
		</>
	);
}

function SalesMapperModal(props: {
	close: () => void;
	open: boolean;
	selectedMapperInfo: IntegrationMapper;
	fileInfo: IntegrationFileInfo;
	mapperConfig: SalesMapperFieldConfig[];
}) {

	const fileHeader: string[] = JSON.parse(props.fileInfo.headers);
	const [salesMapping, setSalesMapping] = useState<SalesMapper[]>([]);
	const [mapperName, setMapperName] = useState<string>("");
	const [fileHeaderValueMapping, setFileHeaderValueMapping] = useState<
		Map<string, string>
	>(new Map());

	const setSalesMappingFieldValue = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
		keyname: string
	) => {
		let newmapping = salesMapping.map((mapping) =>
			mapping.columnName === event.target.name
				? {
						...mapping,
						[keyname]: event.target.value,
				  }
				: mapping
		);
		setSalesMapping(newmapping);
	};

	const parseInput = (value: any) => {
		if (value === undefined || value === null) return "";
		if (value instanceof Array) return value.join(",");
		else if (typeof value === "object") return JSON.stringify(value);
		else return value;
	};

	const saveMapperDetails = async () => {
		const res = validateMapper();
		if(!res) {
			alert("Please map all required fields");
			return;
		}
		let mapperRequestObject: SalesMapperRequest = {
			mapper: JSON.stringify(salesMapping),
			lob: props.fileInfo.lob,
			type: props.fileInfo.entityName,
			name: mapperName,
		};
		if (mapperName === "") {
			alert("Please enter mapper name");
		} else {
			let response = await saveSalesMapper(mapperRequestObject);
			if (response.status === 200 || response.status === 201) {
				props.close();
			} else {
				alert("Error while saving mapper on server.");
			}
		}
	};

	const validateMapper = () => {
		let res = true;
		salesMapping.forEach((element) => {
			if (element.mappedColumn === "" && props.mapperConfig.find((mapper) => mapper.columnName === element.columnName)?.isRequired) {
				res = false;
			}
		});
		return res;
	}

	useEffect(() => {
		const headers = JSON.parse(props.fileInfo.headers);
		const values = JSON.parse(props.fileInfo.firstRecord);
		let fileHeaderMap: Map<string, string> = new Map();
		headers.forEach((element: string, index: number) => {
			fileHeaderMap.set(element, values[index]);
		});
		setFileHeaderValueMapping(fileHeaderMap);
		if (
			props.selectedMapperInfo.mapperName !== defaultMapperInfo.mapperName
		)
			setMapperName(props.selectedMapperInfo.mapperName);
		try {
			const config: any = props.selectedMapperInfo.config;
			if (config instanceof Array) {
				setSalesMapping(config as SalesMapper[]);
				return;
			} else if (typeof config === "string") {
				setSalesMapping(JSON.parse(config) as SalesMapper[]);
				return;
			} else {
				setSalesMapping(config as SalesMapper[]);
				return;
			}
		} catch (e) {
			setSalesMapping(
				props.mapperConfig.map((element) => {
					return {
						columnName: element.columnName,
						mappedColumn: "",
						transFunction: "none",
						formatter: "",
					};
				})
			);
			console.error("Error in parsing config", e);
		}
	}, []);

	let height = 96;
	let width = 70;

	return (
		<Modal
			open={props.open}
			onClose={props.close}
			sx={{
				position: "absolute",
				top: `${(100 - height) / 2}vh`,
				left: `${(100 - width) / 2}vw`,
				width: `${width}vw`,
				height: `${height}vh`,
				overflow: "auto",
				borderRadius: 4,
			}}>
			<Stack
				sx={{ backgroundColor: "#ffffff" }}
				padding={2}>
				<Grid
					container
					spacing={2}
					padding={1}
					justifyContent={"space-between"}>
					<Grid
						item
						xs>
						<Typography
							variant="h6"
							component={"span"}
							marginRight={2}>
							File Name
						</Typography>
						{props.fileInfo.fileName}
					</Grid>
					<Grid item>
						<Button
							variant="outlined"
							onClick={props.close}>
							cancel
						</Button>
					</Grid>
					<Grid item>
						<Button
							variant="outlined"
							onClick={saveMapperDetails}>
							Save
						</Button>
					</Grid>
				</Grid>

				<Box
					marginY={3}
					sx={{ maxWidth: "60%" }}>
					<TextField
						label="Mapper Name"
						placeholder="Enter Mapper Name"
						value={mapperName}
						disabled={
							props.selectedMapperInfo.mapperName !==
							defaultMapperInfo.mapperName
						}
						onChange={(e) =>
							setMapperName(e.target.value)
						}></TextField>
				</Box>

				{props.mapperConfig.map((mapper, idx) => {
					let value: SalesMapper = salesMapping.find(
						(element) => mapper.columnName === element.columnName
					) || {
						columnName: mapper.columnName,
						mappedColumn: "",
						formatter: "",
						transFunction: "none",
					};
					return (
						<Card
							key={idx}
							variant="outlined"
							sx={{ height: "100%", padding: 0, marginY: 1 }}>
							<Grid container>
								<Grid
									item
									xs
									padding={1}
									marginLeft={1}
									style={{
										display: "flex",
										flexDirection: "column",
										justifyContent: "space-evenly",
										alignItems: "start",
									}}>
									<Box>
										{mapper.displayName}{" "}
										{mapper.isRequired && (
											<span style={{ color: "red" }}>
												*
											</span>
										)}
									</Box>
									<Box>
										{value && value.mappedColumn ? (
											<h6
												style={{
													margin: 0,
													padding: "1px 5px",
													borderRadius: "4px",
													backgroundColor:
														"rgb(240, 240, 240)",
													display: "inline-block",
													overflow: "auto",
													wordBreak: "break-all",
													maxWidth: "100%",
													maxHeight: "40px",
												}}>
												{parseInput(
													fileHeaderValueMapping.get(
														value.mappedColumn
													)
												)}
											</h6>
										) : null}
									</Box>
								</Grid>
								{mapper.formatter && (
									<Grid
										item
										marginTop={2}
										xs={4}>
										<TextField
											name={mapper.columnName}
											sx={{ margin: 0 }}
											label="Date Format"
											placeholder="Date Format"
											onChange={(e) =>
												setSalesMappingFieldValue(
													e,
													"formatter"
												)
											}
											value={
												value ? value.formatter : ""
											}></TextField>
									</Grid>
								)}
								<Grid
									item
									marginTop={2}
									paddingX={1}
									xs={5}>
									<TextField
										name={mapper.columnName}
										onChange={(e) =>
											setSalesMappingFieldValue(
												e,
												"mappedColumn"
											)
										}
										sx={{ margin: 0 }}
										select
										label="Data Field"
										value={value ? value.mappedColumn : ""}>
										{fileHeader.map((element) => (
											<MenuItem
												value={element}
												key={element}>
												{element}
											</MenuItem>
										))}
									</TextField>
								</Grid>
							</Grid>
						</Card>
					);
				})}
			</Stack>
		</Modal>
	);
}
